import { useCanEditEmailCampaigns } from '@components/Common/Utils/roles';
import CRMStatusChip from '@components/Common/CRMStatusChip';
import { PageHero, Panel } from '@components/Common/Panel';
import ErrorMessage from '@components/Common/ErrorMessage';
import FormField from '@components/Common/FormField';
import SelectFormField from '@components/Common/SelectFormField';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import MintlyTable from '@components/Common/Table';
import { useToast } from '@components/Common/Toast/ToastProvider';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import GroupIcon from '@mui/icons-material/Group';
import HistoryIcon from '@mui/icons-material/History';
import LoopIcon from '@mui/icons-material/Loop';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PauseIcon from '@mui/icons-material/Pause';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutlined';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import PersonRemoveAlt1Icon from '@mui/icons-material/PersonRemoveAlt1';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import SyncIcon from '@mui/icons-material/Sync';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    Menu,
    MenuItem,
    Switch,
    Tooltip,
    Typography,
} from '@mui/material';
import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import './index.scss';
import ConfirmDialog from '@components/Common/ConfirmDialog';
import { DripBulkAction, dripBulkCopy } from './bulkActions';
import { formatDate, formatDateTime } from '@helpers/date';
import ActionIconButton from '@components/Common/ActionIconButton';
import { activeFlagLabels, progressCell } from './enrollmentCells';

/* The table's columns, named once so the loading skeleton can draw the same number of
   them. A skeleton with its own column count is a skeleton that stops matching. */
const ENROLLMENT_COLUMNS = ['email', 'type', 'status', 'progress', 'enrolledAt', 'lastSent', 'activeFlags', 'actions'];
const STEP_COLUMNS = ['step', 'subject', 'sentAt', 'opens', 'clicks', 'firstOpenAt', 'clickedUrls'];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DripEnrollment {
  _id: string;
  userId: string;
  userEmail: string;
  userType: string;
  campaignType: string;
  status: string;
  currentStep: number;
  sentSteps: number[];
  enrolledAt: string;
  lastSentAt: string | null;
  completedAt: string | null;
  behavioralFlags: Record<string, boolean>;
}

interface DripStats {
  total: number;
  active: number;
  completed: number;
  paused: number;
  unsubscribed: number;
  jobseekers: number;
  employers: number;
}

interface CampaignSettings {
  settingKey: string;
  globalPaused: boolean;
  updatedAt: string | null;
}

interface ClickEvent {
  url: string;
  clickedAt: string;
  ipAddress: string;
  userAgent: string;
}

interface EmailSendRecord {
  id: string;
  enrollmentId: string;
  userId: string;
  userEmail: string;
  templateKey: string;
  step: number | null;
  subject: string;
  sentAt: string;
  openCount: number;
  firstOpenAt: string | null;
  lastOpenAt: string | null;
  clickCount: number;
  firstClickAt: string | null;
  clicks: ClickEvent[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const JOBSEEKER_STEPS = [
  { step: 0, label: 'Step 1: Welcome (Day 0)' },
  { step: 1, label: 'Step 2: Profile tips (Day 1)' },
  { step: 2, label: 'Step 3: Browse jobs (Day 3)' },
  { step: 3, label: 'Step 4: Resume help (Day 5)' },
  { step: 4, label: 'Step 5: Courses promo (Day 7)' },
  { step: 5, label: 'Step 6: Success stories (Day 10)' },
  { step: 6, label: 'Step 7: Re-engagement (Day 14)' },
  { step: 7, label: 'Step 8: Final push (Day 21)' },
];

const EMPLOYER_STEPS = [
  { step: 0, label: 'Step 1: Welcome (Day 0)' },
  { step: 1, label: 'Step 2: Post first job (Day 2)' },
  { step: 2, label: 'Step 3: Premium features (Day 4)' },
  { step: 3, label: 'Step 4: Hiring tips (Day 6)' },
  { step: 4, label: 'Step 5: Job performance (Day 8)' },
  { step: 5, label: 'Step 6: Candidate pool (Day 12)' },
  { step: 6, label: 'Step 7: Re-engagement (Day 16)' },
  { step: 7, label: 'Step 8: Partnership (Day 21)' },
];

// ---------------------------------------------------------------------------

const STATUS_LABEL: Record<string, string> = {
  COMPLETED: 'Completed',
  UNSUBSCRIBED: 'Unsubscribed',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const DripCampaigns: React.FC = () => {
  const { showToast } = useToast();
  // An HR Lead reads Email campaigns (owner, 2026-11-03); every write control is hidden from her
  // and refused on the BFF (emailCampaignsAccess: GET only for her).
  const canEdit = useCanEditEmailCampaigns();

  // ---- Tab state -----------------------------------------------------------
  // Analytics tab removed — drip analytics are now shown on the Dashboard

  // ---- Data state ----------------------------------------------------------
  const [enrollments, setEnrollments] = useState<DripEnrollment[]>([]);
  const [stats, setStats] = useState<DripStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ---- Analytics state removed — moved to Dashboard ----------------------

  // ---- Email history dialog state ------------------------------------------
  const [historyDialog, setHistoryDialog] = useState(false);
  const [historyEnrollment, setHistoryEnrollment] = useState<DripEnrollment | null>(null);
  const [historyRecords, setHistoryRecords] = useState<EmailSendRecord[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // ---- Filters / pagination ------------------------------------------------
  const [filterStatus, setFilterStatus] = useState('');
  const [filterUserType, setFilterUserType] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(0, parseInt(searchParams.get('page') ?? '0', 10));
  const rowsPerPage = 20;
  const [totalElements, setTotalElements] = useState(0);

  // ---- Trigger dialog state ------------------------------------------------
  const [triggerDialog, setTriggerDialog] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState<DripEnrollment | null>(null);
  const [selectedStep, setSelectedStep] = useState<number>(0);
  const [triggerLoading, setTriggerLoading] = useState(false);
  const [overrideEmail, setOverrideEmail] = useState('');

  // ---- Backfill / enroll-by-email state -----------------------------------
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [enrollDialog, setEnrollDialog] = useState(false);
  const [enrollEmail, setEnrollEmail] = useState('');
  const [enrollUserType, setEnrollUserType] = useState<'JOBSEEKER' | 'EMPLOYER'>('JOBSEEKER');
  const [enrollLoading, setEnrollLoading] = useState(false);

  // ---- Global settings / pause --------------------------------------------
  const [globalPaused, setGlobalPaused] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [processLoading, setProcessLoading] = useState(false);

  // ---- Search by email ----------------------------------------------------
  const [searchEmail, setSearchEmail] = useState('');
  /*
   * What the input shows, versus what the query uses.
   *
   * The fetch depends on `searchEmail` and there is no debounce and no request ordering, so typing
   * an address fired a request per keystroke over plain axios: eleven for "ravi@x.com", and a slow
   * early response could land after — and overwrite — a later, correct one. EmailAudit gets away
   * with the same shape only because its saga uses takeLatest.
   */
  const [searchDraft, setSearchDraft] = useState('');

  useEffect(() => {
    const id = setTimeout(() => {
      setSearchEmail(searchDraft);
      setPage(0);
    }, 350);
    return () => clearTimeout(id);
  }, [searchDraft]);

  // ---- Per-enrollment action menu -----------------------------------------
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [actionMenuEnrollment, setActionMenuEnrollment] = useState<DripEnrollment | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // ---- Action menu helpers -------------------------------------------------
  const openActionMenu = (event: React.MouseEvent<HTMLButtonElement>, enrollment: DripEnrollment) => {
    setActionMenuAnchor(event.currentTarget);
    setActionMenuEnrollment(enrollment);
  };
  const closeActionMenu = () => {
    setActionMenuAnchor(null);
    setActionMenuEnrollment(null);
  };

  // ---- Fetch enrollments ---------------------------------------------------
  const fetchEnrollments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number> = { page, size: rowsPerPage };
      if (filterStatus)   params.status   = filterStatus;
      if (filterUserType) params.userType = filterUserType;
      if (searchEmail.trim()) params.email = searchEmail.trim();

      const { data } = await axios.get('/api/drip-campaigns/enrollments', { params });
      setEnrollments(data.content || []);
      setTotalElements(data.totalElements || 0);
    } catch {
      setError('Failed to load enrollments. Please try again.');
      showToast('Failed to load drip campaign enrollments', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filterStatus, filterUserType, searchEmail, showToast]);

  // ---- Fetch stats ---------------------------------------------------------
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const { data } = await axios.get('/api/drip-campaigns/stats');
      setStats(data);
    } catch {
      /* stats are non-critical; fail silently */
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // ---- Fetch global settings -----------------------------------------------
  const fetchSettings = useCallback(async () => {
    try {
      const { data } = await axios.get<CampaignSettings>('/api/drip-campaigns/settings');
      setGlobalPaused(data.globalPaused);
    } catch {
      /* settings are non-critical; fail silently */
    }
  }, []);

  // ---- Fetch analytics (removed) -- now on Dashboard ----------------------

  useEffect(() => {
    fetchEnrollments();
    fetchStats();
    fetchSettings();
  }, [fetchEnrollments, fetchStats, fetchSettings]);

  // ---- Email history -------------------------------------------------------
  const openEmailHistory = async (enrollment: DripEnrollment) => {
    setHistoryEnrollment(enrollment);
    setHistoryDialog(true);
    setHistoryLoading(true);
    try {
      const { data } = await axios.get(
        `/api/drip-campaigns/enrollments/${enrollment._id}/email-history`
      );
      setHistoryRecords(Array.isArray(data) ? data : []);
    } catch {
      showToast('Failed to load email history', 'error');
      setHistoryRecords([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const closeEmailHistory = () => {
    setHistoryDialog(false);
    setHistoryEnrollment(null);
    setHistoryRecords([]);
  };

  // ---- Trigger dialog handlers -------------------------------------------
  const openTriggerDialog = (enrollment: DripEnrollment) => {
    setSelectedEnrollment(enrollment);
    setSelectedStep(enrollment.currentStep < 8 ? enrollment.currentStep : 0);
    setOverrideEmail(enrollment.userEmail);
    setTriggerDialog(true);
  };

  const closeTriggerDialog = () => {
    setTriggerDialog(false);
    setSelectedEnrollment(null);
  };

  const handleTrigger = async () => {
    if (!selectedEnrollment) return;
    setTriggerLoading(true);
    try {
      const payload = {
        userId:       selectedEnrollment.userId,
        campaignType: selectedEnrollment.campaignType,
        step:         selectedStep,
      };
      const { data } = await axios.post('/api/drip-campaigns/trigger', payload);
      showToast(`Email sent to ${data.email ?? overrideEmail} (Step ${selectedStep + 1})`, 'success');
      closeTriggerDialog();
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to trigger email';
      showToast(msg, 'error');
    } finally {
      setTriggerLoading(false);
    }
  };

  // ---- Backfill -----------------------------------------------------------
  const handleBackfill = async () => {
    setBackfillLoading(true);
    try {
      await axios.post('/api/drip-campaigns/backfill');
      showToast('Backfill triggered: all existing users are being enrolled', 'success');
      fetchEnrollments();
      fetchStats();
    } catch {
      showToast('Backfill failed. Please try again.', 'error');
    } finally {
      setBackfillLoading(false);
    }
  };

  const handleEnrollByEmail = async () => {
    if (!enrollEmail.trim()) return;
    setEnrollLoading(true);
    try {
      const { data } = await axios.post('/api/drip-campaigns/enroll-by-email', {
        email: enrollEmail.trim(),
        userType: enrollUserType,
      });
      const isJobseeker = enrollUserType === 'JOBSEEKER';
      const found    = isJobseeker ? data.jobseekerFound    : data.employerFound;
      const enrolled = isJobseeker ? data.jobseekerEnrolled : data.employerEnrolled;
      const label    = isJobseeker ? 'Job Seeker' : 'Employer';
      const msg = !found
        ? `No ${label} record found for ${enrollEmail}`
        : enrolled
          ? `${enrollEmail} enrolled as ${label}`
          : `${enrollEmail} is already enrolled as ${label}`;
      showToast(msg, found ? 'success' : 'warning');
      if (found) {
        setEnrollDialog(false);
        setEnrollEmail('');
        setEnrollUserType('JOBSEEKER');
        fetchEnrollments();
        fetchStats();
      }
    } catch {
      showToast('Enrollment failed. Please try again.', 'error');
    } finally {
      setEnrollLoading(false);
    }
  };

  // ---- Pagination ----------------------------------------------------------
  const setPage = (newPage: number) => {
    // Page 0 is the default, so it is left out of the URL rather than written as `?page=0`: the
    // table reports page 0 as it mounts, and writing it raced the email-campaigns alias redirect
    // (every panel mounts at once) and put a query on a URL that had none.
    if (String(newPage) === (searchParams.get('page') ?? '0')) return;
    setSearchParams(prev => {
      if (newPage === 0) prev.delete('page'); else prev.set('page', String(newPage));
      return prev;
    }, { replace: true });
  };
  const handleChangePage = (_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => setPage(newPage);

  // ---- Global pause toggle -------------------------------------------------
  const handleToggleGlobalPause = async () => {
    setSettingsLoading(true);
    const newPaused = !globalPaused;
    try {
      await axios.put('/api/drip-campaigns/settings', { globalPaused: newPaused });
      setGlobalPaused(newPaused);
      showToast(newPaused ? 'Campaign paused: no emails will be sent' : 'Campaign resumed', newPaused ? 'warning' : 'success');
    } catch {
      showToast('Failed to update campaign settings', 'error');
    } finally {
      setSettingsLoading(false);
    }
  };

  // ---- Account-wide actions go through one confirmation ---------------------
  const [pendingAction, setPendingAction] = useState<DripBulkAction | null>(null);
  const pendingCopy = pendingAction ? dripBulkCopy(pendingAction, stats?.active) : null;
  const confirmPending = async () => {
    const action = pendingAction;
    setPendingAction(null);
    if (action === 'run') await handleProcessNow();
    else if (action === 'backfill') await handleBackfill();
    else if (action === 'pause' || action === 'resume') await handleToggleGlobalPause();
  };

  // ---- Run Now -------------------------------------------------------------
  const handleProcessNow = async () => {
    setProcessLoading(true);
    try {
      await axios.post('/api/drip-campaigns/process');
      showToast('Drip processor ran successfully', 'success');
      fetchEnrollments();
      fetchStats();
    } catch {
      showToast('Failed to run processor. Check backend logs.', 'error');
    } finally {
      setProcessLoading(false);
    }
  };

  // ---- Per-enrollment actions ---------------------------------------------
  const handleEnrollmentAction = async (enrollment: DripEnrollment, action: string) => {
    const key = `${enrollment._id}:${action}`;
    setActionLoading(key);
    closeActionMenu();
    try {
      const { data } = await axios.post<DripEnrollment>(
        `/api/drip-campaigns/enrollments/${enrollment._id}/${action}`
      );
      setEnrollments(prev => prev.map(e => e._id === enrollment._id ? { ...e, status: data.status, currentStep: data.currentStep, completedAt: data.completedAt } : e));
      const labels: Record<string, string> = {
        pause: 'Paused',
        resume: 'Resumed',
        unsubscribe: 'Unsubscribed',
        're-enroll': 'Re-enrolled from step 1',
      };
      showToast(`${enrollment.userEmail}: ${labels[action] ?? action}`, 'success');
    } catch {
      showToast(`Failed to ${action} enrollment`, 'error');
    } finally {
      setActionLoading(null);
    }
  };

  // ---- Step labels ---------------------------------------------------------
  const getStepOptions = (enrollment: DripEnrollment | null) =>
    enrollment?.userType === 'JOBSEEKER' ? JOBSEEKER_STEPS : EMPLOYER_STEPS;

  /* The date alone keeps the column narrow; the time is one hover away. No tooltip on an empty
     value: a never-sent row would otherwise offer a "-" on hover. */
  const dateCell = (value: string | null) => {
    const cell = <Typography variant="body2" className="drip-date">{formatDate(value)}</Typography>;
    return value ? <Tooltip title={formatDateTime(value)} placement="top">{cell}</Tooltip> : cell;
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <Box className="drip-campaigns-container">
      <Box className="ae-page-body max-w-container">
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <PageHero
        kicker="Drip Campaigns"
        title="Manage automated email sequences for jobseekers and employers."
        actions={(
          <>
            {canEdit && (
            <Tooltip title={globalPaused ? 'Campaign is globally paused. Click to resume' : 'Click to pause all campaign sends'}>
              <FormControlLabel
                control={(
                  <Switch
                    checked={!globalPaused}
                    onChange={() => setPendingAction(globalPaused ? 'resume' : 'pause')}
                    disabled={settingsLoading}
                    color="success"
                    size="small"
                  />
                )}
                label={(
                  <Typography variant="caption" sx={{ fontWeight: 600, color: globalPaused ? 'var(--amber)' : 'var(--green)' }}>
                    {globalPaused ? 'Paused' : 'Active'}
                  </Typography>
                )}
                sx={{ mr: 0.5 }}
              />
            </Tooltip>
            )}
            {canEdit && (
              <Button size="small" variant="outlined" startIcon={<PlayArrowIcon />} onClick={() => setPendingAction('run')} disabled={processLoading || globalPaused}>
                {processLoading ? 'Running…' : 'Run Now'}
              </Button>
            )}
            {canEdit && (
              <Button size="small" variant="outlined" startIcon={<PersonAddAltIcon />} onClick={() => setEnrollDialog(true)}>
                Enroll by Email
              </Button>
            )}
            {canEdit && (
              <Button size="small" variant="outlined" startIcon={<SyncIcon />} onClick={() => setPendingAction('backfill')} disabled={backfillLoading}>
                {backfillLoading ? 'Running…' : 'Backfill All'}
              </Button>
            )}
            <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={() => { fetchEnrollments(); fetchStats(); }}>
              Refresh
            </Button>
          </>
        )}
      />

      {/* ---- Stats Cards ---- */}
      <Grid container spacing={2} className="drip-stats-grid">
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <Panel variant="stat" className="drip-stat-card">
            <Box className="drip-stat-content">
              <Box className="drip-stat-icon drip-stat-icon--total"><GroupIcon fontSize="small" /></Box>
              <Typography variant="h5" className="drip-stat-number">{statsLoading ? '-' : (stats?.total ?? 0)}</Typography>
              <Typography variant="caption" className="drip-stat-label">Total</Typography>
            </Box>
          </Panel>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <Panel variant="stat" className="drip-stat-card">
            <Box className="drip-stat-content">
              <Box className="drip-stat-icon drip-stat-icon--active"><PlayCircleOutlineIcon fontSize="small" /></Box>
              <Typography variant="h5" className="drip-stat-number drip-stat-number--active">{statsLoading ? '-' : (stats?.active ?? 0)}</Typography>
              <Typography variant="caption" className="drip-stat-label">Active</Typography>
            </Box>
          </Panel>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <Panel variant="stat" className="drip-stat-card">
            <Box className="drip-stat-content">
              <Box className="drip-stat-icon drip-stat-icon--completed"><CheckCircleOutlineIcon fontSize="small" /></Box>
              <Typography variant="h5" className="drip-stat-number drip-stat-number--completed">{statsLoading ? '-' : (stats?.completed ?? 0)}</Typography>
              <Typography variant="caption" className="drip-stat-label">Completed</Typography>
            </Box>
          </Panel>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <Panel variant="stat" className="drip-stat-card">
            <Box className="drip-stat-content">
              <Box className="drip-stat-icon drip-stat-icon--paused"><PauseCircleOutlineIcon fontSize="small" /></Box>
              <Typography variant="h5" className="drip-stat-number drip-stat-number--paused">{statsLoading ? '-' : (stats?.paused ?? 0)}</Typography>
              <Typography variant="caption" className="drip-stat-label">Paused</Typography>
            </Box>
          </Panel>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <Panel variant="stat" className="drip-stat-card">
            <Box className="drip-stat-content">
              <Box className="drip-stat-icon drip-stat-icon--jobseeker"><PersonSearchIcon fontSize="small" /></Box>
              <Typography variant="h5" className="drip-stat-number drip-stat-number--jobseeker">{statsLoading ? '-' : (stats?.jobseekers ?? 0)}</Typography>
              <Typography variant="caption" className="drip-stat-label">Job Seekers</Typography>
            </Box>
          </Panel>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <Panel variant="stat" className="drip-stat-card">
            <Box className="drip-stat-content">
              <Box className="drip-stat-icon drip-stat-icon--employer"><WorkOutlineOutlinedIcon fontSize="small" /></Box>
              <Typography variant="h5" className="drip-stat-number drip-stat-number--employer">{statsLoading ? '-' : (stats?.employers ?? 0)}</Typography>
              <Typography variant="caption" className="drip-stat-label">Employers</Typography>
            </Box>
          </Panel>
        </Grid>
      </Grid>

      {/* ---- Global Pause Banner ---- */}
      {globalPaused && (
        <Alert
          severity="warning"
          icon={<WarningAmberIcon />}
          sx={{ mb: 2, borderRadius: 'var(--border-radius-lg)' }}
          action={canEdit ? (
            <Button color="warning" size="small" variant="outlined" startIcon={<PlayArrowIcon />}
              onClick={() => setPendingAction('resume')} disabled={settingsLoading}>
              Resume Campaign
            </Button>
          ) : undefined}
        >
          <strong>Drip campaign is globally paused.</strong> No emails will be sent by the scheduler until you resume.
        </Alert>
      )}

      {/* ---- Enrollments ---- */}
      <>
          {/* ---- Filters ---- */}
          <Card className="drip-filter-card">
            <CardContent>
              <Box className="drip-filters-header">
                <Typography variant="subtitle2" className="drip-filter-label">Filters</Typography>
                <Typography variant="caption" className="drip-results-count">
                  {totalElements} enrollment{totalElements !== 1 ? 's' : ''}
                </Typography>
              </Box>
              <Box className="drip-filters">
                <Box className="drip-filter-inputs">
                  <Box className="drip-filter-item">
                    <SelectFormField
                      required={false}
                      name="filterStatus"
                      label="Status"
                      value={filterStatus}
                      onChange={(_name, val) => { setFilterStatus(String(val)); setPage(0); }}
                      options={[
                        { value: '', label: 'All Statuses' },
                        { value: 'ACTIVE', label: 'Active' },
                        { value: 'COMPLETED', label: 'Completed' },
                        { value: 'PAUSED', label: 'Paused' },
                        { value: 'UNSUBSCRIBED', label: 'Unsubscribed' },
                      ]}
                      size="small"
                      fullWidth
                    />
                  </Box>
                  <Box className="drip-filter-item">
                    <SelectFormField
                      required={false}
                      name="filterUserType"
                      label="User Type"
                      value={filterUserType}
                      onChange={(_name, val) => { setFilterUserType(String(val)); setPage(0); }}
                      options={[
                        { value: '', label: 'All Users' },
                        { value: 'JOBSEEKER', label: 'Job Seeker' },
                        { value: 'EMPLOYER', label: 'Employer' },
                      ]}
                      size="small"
                      fullWidth
                    />
                  </Box>
                  <Box className="drip-filter-item">
                    <FormField
                      required={false}
                      name="searchEmail"
                      label="Search by email"
                      value={searchDraft}
                      onChange={(_name, val) => setSearchDraft(String(val))}
                      placeholder="filter@email.com"
                      size="small"
                      type="email"
                      fullWidth
                    />
                  </Box>
                </Box>
                {(filterStatus || filterUserType || searchEmail) && (
                  <Button size="small" variant="text"
                    onClick={() => { setFilterStatus(''); setFilterUserType(''); setSearchDraft(''); setSearchEmail(''); setPage(0); }}
                    className="drip-clear-filters-btn">
                    Clear Filters
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* ---- Table ---- */}
          {error ? (
            <Box sx={{ mt: 2 }}><ErrorMessage message={error} /></Box>
          ) : loading ? (
            <Card className="drip-table-loading-card">
              <Box sx={{ p: 2 }}><SkeletonLoader rows={8} columns={ENROLLMENT_COLUMNS.length} /></Box>
            </Card>
          ) : (
            <Box>
              <MintlyTable
                rows={enrollments}
                selectors={ENROLLMENT_COLUMNS}
                tableHeadRow={{
                  email:       'Email',
                  type:        'Type',
                  status:      'Status',
                  progress:    'Progress',
                  enrolledAt:  'Enrolled',
                  lastSent:    'Last sent',
                  activeFlags: 'Flags',
                  actions:     'Actions',
                }}
                tableBodyRow={{
                  email: (row) => (
                    <Tooltip title={row.userId} placement="top">
                      <Box className="drip-email-box">
                        <EmailIcon className="drip-email-icon" fontSize="small" />
                        <span>{row.userEmail || '-'}</span>
                      </Box>
                    </Tooltip>
                  ),
                  /* A audience, not a status: this one keeps its own chip and its own tokens. */
                  type: (row) => (
                    <Chip
                      size="small"
                      label={row.userType === 'JOBSEEKER' ? 'Job Seeker' : 'Employer'}
                      className={`drip-type-chip drip-type-chip--${row.userType?.toLowerCase()}`}
                    />
                  ),
                  /* COMPLETED and UNSUBSCRIBED are enrolment outcomes rather than states of
                     the record, so they carry a label; ACTIVE and PAUSED resolve on their own. */
                  status: (row) => <CRMStatusChip status={row.status} label={STATUS_LABEL[row.status]} />,
                  /* One column for one fact: where the sequence is, and how much has gone out. */
                  progress: (row) => {
                    const p = progressCell(row.status, row.currentStep, row.sentSteps);
                    return (
                      <Box className="drip-progress">
                        <Typography variant="body2" className={`drip-step-badge${p.done ? ' drip-step-badge--done' : ''}`}>
                          {p.primary}
                        </Typography>
                        <Typography variant="caption" className="drip-progress__sent">{p.secondary}</Typography>
                      </Box>
                    );
                  },
                  /* The date alone keeps the column narrow; the time is one hover away. */
                  enrolledAt: (row) => dateCell(row.enrolledAt),
                  lastSent: (row) => dateCell(row.lastSentAt),
                  /* A count, with the readable names on hover, instead of truncated raw keys. */
                  activeFlags: (row) => {
                    const labels = activeFlagLabels(row.behavioralFlags);
                    if (labels.length === 0) {
                      return <Typography variant="caption" className="drip-flags">None</Typography>;
                    }
                    return (
                      <Tooltip title={labels.join(', ')} placement="top">
                        <Typography variant="caption" className="drip-flags">
                          {labels.length === 1 ? labels[0] : `${labels.length} flags`}
                        </Typography>
                      </Tooltip>
                    );
                  },
                  actions: (row) => {
                    const isLoadingAction = actionLoading?.startsWith(row._id);
                    return (
                      <Box className="drip-row-actions">
                        {canEdit && (
                          <ActionIconButton
                            label="Send a drip email"
                            icon={<SendIcon fontSize="small" />}
                            color="primary"
                            size="small"
                            busy={isLoadingAction}
                            onClick={() => openTriggerDialog(row)}
                          />
                        )}
                        <ActionIconButton
                          label="More actions"
                          icon={<MoreVertIcon fontSize="small" />}
                          size="small"
                          disabled={isLoadingAction}
                          onClick={(e) => openActionMenu(e, row)}
                          className="drip-action-menu-btn"
                        />
                      </Box>
                    );
                  },
                }}
                pagination={{
                  enabled: true,
                  page,
                  rowsPerPage,
                  totalNumberOfRows: totalElements,
                  handleChangePage,
                }}
              />
            </Box>
          )}

          {/* ---- Email Templates Reference Card ---- */}
          <Card className="drip-templates-card">
            <CardContent>
              <Typography variant="h6" className="drip-templates-title">Email Templates Reference</Typography>
              <Grid container spacing={3} sx={{ mt: 0.5 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" className="drip-templates-track-title">
                    Job Seeker Welcome Track (8 emails)
                  </Typography>
                  <Box className="drip-templates-list">
                    {JOBSEEKER_STEPS.map(({ step, label }) => (
                      <Box key={step} className="drip-template-item">
                        <Box className="drip-template-step">{step + 1}</Box>
                        <Typography variant="caption">{label}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" className="drip-templates-track-title">
                    Employer Welcome Track (8 emails)
                  </Typography>
                  <Box className="drip-templates-list">
                    {EMPLOYER_STEPS.map(({ step, label }) => (
                      <Box key={step} className="drip-template-item">
                        <Box className="drip-template-step drip-template-step--employer">{step + 1}</Box>
                        <Typography variant="caption">{label}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
      </>

      {/* ---- Per-enrollment Action Menu ---- */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={closeActionMenu}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => { openEmailHistory(actionMenuEnrollment!); closeActionMenu(); }}>
          <HistoryIcon fontSize="small" sx={{ mr: 1, color: 'var(--indigo)' }} />
          Email History
        </MenuItem>
        {canEdit && <Divider sx={{ my: 0.5 }} />}
        {canEdit && actionMenuEnrollment?.status === 'ACTIVE' && (
          <MenuItem onClick={() => handleEnrollmentAction(actionMenuEnrollment, 'pause')}>
            <PauseIcon fontSize="small" sx={{ mr: 1, color: 'var(--amber)' }} />
            Pause
          </MenuItem>
        )}
        {canEdit && actionMenuEnrollment?.status === 'PAUSED' && (
          <MenuItem onClick={() => handleEnrollmentAction(actionMenuEnrollment, 'resume')}>
            <PlayArrowIcon fontSize="small" sx={{ mr: 1, color: 'var(--green)' }} />
            Resume
          </MenuItem>
        )}
        {canEdit && actionMenuEnrollment?.status !== 'UNSUBSCRIBED' && (
          <MenuItem onClick={() => handleEnrollmentAction(actionMenuEnrollment!, 'unsubscribe')}>
            <PersonRemoveAlt1Icon fontSize="small" sx={{ mr: 1, color: 'var(--red)' }} />
            Unsubscribe
          </MenuItem>
        )}
        {canEdit && (actionMenuEnrollment?.status === 'COMPLETED' ||
          actionMenuEnrollment?.status === 'PAUSED' ||
          actionMenuEnrollment?.status === 'UNSUBSCRIBED') && (
          <MenuItem onClick={() => handleEnrollmentAction(actionMenuEnrollment!, 're-enroll')}>
            <LoopIcon fontSize="small" sx={{ mr: 1, color: 'var(--acc)' }} />
            Re-enroll from Step 1
          </MenuItem>
        )}
      </Menu>

      {/* ---- Email History Dialog ---- */}
      <Dialog
        open={historyDialog}
        onClose={closeEmailHistory}
        maxWidth="md"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: 3, maxHeight: '85vh' } } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon sx={{ color: 'var(--indigo)' }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Email History
              </Typography>
              {historyEnrollment && (
                <Typography variant="caption" color="text.secondary">
                  {historyEnrollment.userEmail}: {historyEnrollment.campaignType === 'JOBSEEKER_WELCOME' ? 'Jobseeker' : 'Employer'} campaign
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton size="small" onClick={closeEmailHistory}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {historyLoading ? (
            <Box sx={{ p: 3 }}><SkeletonLoader rows={5} columns={STEP_COLUMNS.length} /></Box>
          ) : historyRecords.length === 0 ? (
            <Box sx={{ py: 5, textAlign: 'center', color: 'var(--tx-low)' }}>
              <EmailIcon sx={{ fontSize: 40, mb: 1, opacity: 0.4 }} />
              <Typography variant="body2">No emails sent yet for this enrollment</Typography>
            </Box>
          ) : (
            <MintlyTable
              rows={historyRecords}
              selectors={STEP_COLUMNS}
              tableHeadRow={{
                step: 'Step',
                subject: 'Subject',
                sentAt: 'Sent At',
                opens: 'Opens',
                clicks: 'Clicks',
                firstOpenAt: 'First Open',
                clickedUrls: 'Clicked URLs',
              }}
              tableBodyRow={{
                step: (r: any) => (
                  <Chip
                    size="small"
                    className="drip-history__step"
                    label={r.step !== null ? `Step ${(r.step ?? 0) + 1}` : 'Transactional'}
                  />
                ),
                subject: (r: any) => (
                  <Tooltip title={r.subject} placement="top">
                    <span className="drip-history__subject">{r.subject || '-'}</span>
                  </Tooltip>
                ),
                sentAt: (r: any) => (
                  <span className="drip-history__when">
                    {formatDateTime(r.sentAt)}
                  </span>
                ),
                opens: (r: any) => (
                  <Box className="drip-history__metric">
                    <VisibilityIcon className={`drip-history__icon${r.openCount > 0 ? ' is-on' : ''}`} />
                    <Typography variant="body2" className={`drip-history__count${r.openCount > 0 ? ' is-open' : ''}`}>
                      {r.openCount}
                    </Typography>
                  </Box>
                ),
                clicks: (r: any) => (
                  <Box className="drip-history__metric">
                    <TouchAppIcon className={`drip-history__icon${r.clickCount > 0 ? ' is-click' : ''}`} />
                    <Typography variant="body2" className={`drip-history__count${r.clickCount > 0 ? ' is-click' : ''}`}>
                      {r.clickCount}
                    </Typography>
                  </Box>
                ),
                firstOpenAt: (r: any) => (
                  <span className="drip-history__when">
                    {formatDateTime(r.firstOpenAt)}
                  </span>
                ),
                clickedUrls: (r: any) =>
                  r.clicks && r.clicks.length > 0 ? (
                    <Box className="drip-history__urls">
                      {r.clicks.slice(0, 3).map((click: any, i: number) => (
                        <Tooltip key={i} title={`${click.url}\n${formatDateTime(click.clickedAt)}`} placement="top">
                          <Typography variant="caption" className="drip-history__url">
                            {click.url.replace(/^https?:\/\/[^/]+/, '')}
                          </Typography>
                        </Tooltip>
                      ))}
                      {r.clicks.length > 3 && (
                        <Typography variant="caption" className="drip-history__more">
                          +{r.clicks.length - 3} more
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.secondary">-</Typography>
                  ),
              }}
              pagination={{ enabled: false, page: 0, rowsPerPage: historyRecords.length || 1, totalNumberOfRows: historyRecords.length, handleChangePage: () => {} }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            {historyRecords.length} email{historyRecords.length !== 1 ? 's' : ''} in history
          </Typography>
          <Button onClick={closeEmailHistory} variant="outlined" size="small">Close</Button>
        </DialogActions>
      </Dialog>

      {/* ---- Enroll by Email Dialog ---- */}
      <Dialog
        open={enrollDialog}
        onClose={() => { setEnrollDialog(false); setEnrollEmail(''); setEnrollUserType('JOBSEEKER'); }}
        maxWidth="xs"
        fullWidth
        slotProps={{ paper: { className: 'drip-trigger-dialog' } }}
      >
        <DialogTitle className="drip-dialog-title">
          <Box className="drip-dialog-title-row">
            <PersonAddAltIcon className="drip-dialog-title-icon" />
            <span>Enroll by Email</span>
          </Box>
        </DialogTitle>
        <DialogContent className="drip-dialog-content">
          <DialogContentText className="drip-dialog-desc">
            Looks up this email in the selected collection and force-enrolls the matching
            record. Bypasses notification preference. Use for testing and admin accounts.
          </DialogContentText>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <FormField
              required
              name="enrollEmail"
              label="Email Address"
              value={enrollEmail}
              onChange={(_name, val) => setEnrollEmail(String(val))}
              type="email"
              placeholder="e.g. user@example.com"
              size="small"
              className="drip-dialog-field"
              textFieldProps={{ autoFocus: true, onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter') handleEnrollByEmail(); } }}
            />
            <SelectFormField
              required
              name="enrollUserType"
              label="Enroll as"
              value={enrollUserType}
              onChange={(_name, val) => setEnrollUserType(val as 'JOBSEEKER' | 'EMPLOYER')}
              options={[
                { value: 'JOBSEEKER', label: 'Job Seeker' },
                { value: 'EMPLOYER', label: 'Employer' },
              ]}
              size="small"
              className="drip-dialog-field"
            />
          </Box>
        </DialogContent>
        <DialogActions className="drip-dialog-actions">
          <Button onClick={() => { setEnrollDialog(false); setEnrollEmail(''); setEnrollUserType('JOBSEEKER'); }}
            variant="outlined" disabled={enrollLoading}>
            Cancel
          </Button>
          <Button onClick={handleEnrollByEmail} variant="contained" disabled={enrollLoading || !enrollEmail.trim()}
            startIcon={<PersonAddAltIcon />} className="drip-dialog-send-btn">
            {enrollLoading ? 'Enrolling...' : 'Enroll'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Manual Trigger Dialog ---- */}
      <Dialog
        open={triggerDialog}
        onClose={closeTriggerDialog}
        maxWidth="sm"
        fullWidth
        slotProps={{ paper: { className: 'drip-trigger-dialog' } }}
      >
        <DialogTitle className="drip-dialog-title">
          <Box className="drip-dialog-title-row">
            <SendIcon className="drip-dialog-title-icon" />
            <span>Manually Send Drip Email</span>
          </Box>
        </DialogTitle>
        <DialogContent className="drip-dialog-content">
          <DialogContentText className="drip-dialog-desc">
            This will immediately send the selected email step to the user. Use this for testing or manual overrides.
          </DialogContentText>
          {selectedEnrollment && (
            <Box className="drip-dialog-fields">
              <FormField
                required={false}
                name="recipientEmail"
                label="Recipient Email"
                /* Disabled: it shows the enrolled user's address rather than accepting one, so
                   it takes no asterisk. */
                value={overrideEmail}
                onChange={() => {}}
                disabled
                size="small"
                className="drip-dialog-field"
              />
              <FormField
                required={false}
                name="userType"
                label="User Type"
                value={selectedEnrollment.userType === 'JOBSEEKER' ? 'Job Seeker' : 'Employer'}
                onChange={() => {}}
                disabled
                size="small"
                className="drip-dialog-field"
              />
              <SelectFormField
                required
                name="selectedStep"
                label="Email Step to Send"
                value={String(selectedStep)}
                onChange={(_name, val) => setSelectedStep(Number(val))}
                options={getStepOptions(selectedEnrollment).map(({ step, label }) => ({ value: String(step), label }))}
                size="small"
                className="drip-dialog-field"
              />
              <Box className="drip-dialog-info">
                <Typography variant="caption" color="text.secondary">
                  Current enrollment: Step {selectedEnrollment.currentStep + 1} of 8 &nbsp;·&nbsp;
                  Status: <strong>{selectedEnrollment.status}</strong>
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions className="drip-dialog-actions">
          <Button onClick={closeTriggerDialog} variant="outlined" disabled={triggerLoading}>
            Cancel
          </Button>
          <Button onClick={handleTrigger} variant="contained" disabled={triggerLoading}
            startIcon={<SendIcon />} className="drip-dialog-send-btn">
            {triggerLoading ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={pendingAction !== null}
        title={pendingCopy?.title ?? ''}
        body={pendingCopy?.body ?? ''}
        actionLabel={pendingCopy?.actionLabel}
        onCancel={() => setPendingAction(null)}
        onConfirm={confirmPending}
      />
      </Box>{/* ae-page-body */}
    </Box>
  );
};

export default DripCampaigns;
